import React from "react";
import { Modal } from "react-bootstrap";
import Resources from "../../../types/Resources";
import ChecklistRadioOptions from "../../Common/ChecklistRadioOptions";
import { HeavyLiftingChecklistFormState } from "../../../types/forms/Permits/Conditions/HeavyLiftingChecklistFormState";
import SignatureDisplay from "../../Signatures/SignatureDisplay";
import BaseUserInfo from "../../../types/BaseUserInfo";
import { SignatureManager } from "../../../hooks/useSignature";
import RadioButton from "../../Common/RadioButton";
import LabelCheckbox from "../../Common/LabelCheckbox";

type HeavyLiftingChecklistModalProps = {
    checklistVersion: number;
    resources: Resources;
    formState: HeavyLiftingChecklistFormState;
    show: boolean;
    baseUserInfo?: BaseUserInfo;
    onChange: (fn: (prevValue: HeavyLiftingChecklistFormState) => HeavyLiftingChecklistFormState) => void;
    onHide: () => void;
    craneOperatorSignatureManager: SignatureManager | undefined;
    canManageContent: boolean;
};

function HeavyLiftingChecklistModal({
    checklistVersion,
    resources,
    formState,
    baseUserInfo,
    show,
    craneOperatorSignatureManager,
    onChange,
    onHide,
    canManageContent
}: HeavyLiftingChecklistModalProps) {
    function changeHandler(key: keyof HeavyLiftingChecklistFormState, value: any) {
        onChange((prevValue) => {
            const updatedFormState: HeavyLiftingChecklistFormState = {
                ...prevValue,
                [key]: { ...prevValue[key], value }
            };

            return updatedFormState;
        });
    }

    const showCraneOperatorSignature = !!craneOperatorSignatureManager;

    return (
        <Modal show={show} onHide={onHide} size="xl">
            <Modal.Header closeButton>
                <h1 className="h5">{resources.get("CheckListHeavyLifts")}</h1>
            </Modal.Header>
            <Modal.Body>
                {showCraneOperatorSignature ? (
                    <SignatureDisplay
                        signatureManager={craneOperatorSignatureManager}
                        resources={resources}
                        signatureLabel={resources.get("CraneOperator")}
                    />
                ) : null}
                {checklistVersion === 0 ? (
                    <ChecklistVersion0
                        canManageContent={canManageContent}
                        changeHandler={changeHandler}
                        formState={formState}
                        resources={resources}
                    />
                ) : (
                    <ChecklistVersion202602
                        canManageContent={canManageContent}
                        changeHandler={changeHandler}
                        formState={formState}
                        resources={resources}
                    />
                )}
            </Modal.Body>
        </Modal>
    );
}

type ChecklistProps = {
    resources: Resources;
    formState: HeavyLiftingChecklistFormState;
    canManageContent: boolean;
    changeHandler: (key: keyof HeavyLiftingChecklistFormState, value: any) => void;
};

function ChecklistVersion0({ resources, formState, changeHandler, canManageContent }: ChecklistProps) {
    return (
        <ul className="list-unstyled">
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-infra-contacted"
                    labelText={resources.get("IsInfraContacted")}
                    onChange={(updatedValue) => changeHandler("isInfraContacted", updatedValue)}
                    resources={resources}
                    value={formState.isInfraContacted.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-approved-by-infra"
                    labelText={resources.get("IsApprovedByInfra")}
                    onChange={(updatedValue) => changeHandler("isApprovedByInfra", updatedValue)}
                    resources={resources}
                    value={formState.isApprovedByInfra.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2 d-flex justify-content-between">
                <span className="w-50">{resources.get("IfApprovedByInfraName")}</span>
                <div className="d-flex align-items-center">
                    <label htmlFor="if-approved-by-infra-name">{resources.get("Name")}</label>
                    <input
                        id="if-approved-by-infra-name"
                        className={`form-control ms-1 me-3 ${!canManageContent ? "read-only-input" : ""}`}
                        type="text"
                        style={{ width: "140px" }}
                        value={formState.ifApprovedByInfraName.value ?? ""}
                        onChange={(e) => changeHandler("ifApprovedByInfraName", e.target.value)}
                        readOnly={!canManageContent}
                    />
                    <label htmlFor="if-approved-by-infra-number">{resources.get("Tel")}</label>
                    <input
                        id="if-approved-by-infra-number"
                        className={`form-control ms-1 ${!canManageContent ? "read-only-input" : ""}`}
                        type="text"
                        style={{ width: "140px" }}
                        placeholder={resources.get("PhoneNumberInputPlaceholder")}
                        value={formState.ifApprovedByInfraNumber.value ?? ""}
                        onChange={(e) => changeHandler("ifApprovedByInfraNumber", e.target.value)}
                        readOnly={!canManageContent}
                    />
                </div>
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="has-infra-done-exposures-on-quay"
                    labelText={resources.get("HasInfraDoneExposuresOnQuay")}
                    onChange={(updatedValue) => changeHandler("hasInfraDoneExposuresOnQuay", updatedValue)}
                    resources={resources}
                    value={formState.hasInfraDoneExposuresOnQuay.value}
                    readOnly={!canManageContent}
                />
                <p className="w-50">
                    <small>{resources.get("HasInfraDoneExposuresOnQuayInfo")}</small>
                </p>
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-lift-line-in-good-condition"
                    labelText={resources.get("IsLiftLineInGoodCondition")}
                    onChange={(updatedValue) => changeHandler("isLiftLineInGoodCondition", updatedValue)}
                    resources={resources}
                    value={formState.isLiftLineInGoodCondition.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-lift-line-correct-for-the-weight"
                    labelText={resources.get("IsLiftLineCorrectForTheWeight")}
                    onChange={(updatedValue) => changeHandler("isLiftLineCorrectForTheWeight", updatedValue)}
                    resources={resources}
                    value={formState.isLiftLineCorrectForTheWeight.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-crane-operator-educated"
                    labelText={resources.get("IsCrainOperatorEducated")}
                    onChange={(updatedValue) => changeHandler("isCrainOperatorEducated", updatedValue)}
                    resources={resources}
                    value={formState.isCrainOperatorEducated.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-equipment-tested"
                    labelText={resources.get("IsEquipmentTested")}
                    onChange={(updatedValue) => changeHandler("isEquipmentTested", updatedValue)}
                    resources={resources}
                    value={formState.isEquipmentTested.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <div className="d-flex justify-content-between form-group">
                    <div>{resources.get("IsLiftAreaShutOff")}</div>
                    <div className="d-flex">
                        <RadioButton
                            checked={formState.isLiftAreaShutOff.value === "ClosedAreaTape"}
                            onSelect={() => changeHandler("isLiftAreaShutOff", "ClosedAreaTape")}
                            label={resources.get("ClosedAreaTape")}
                            readOnly={!canManageContent}
                            inputId="is-lift-area-shut-off__closed-area-tape"
                            name="is-lift-area-shut-off"
                            containerClassName="me-3"
                        />

                        <RadioButton
                            checked={formState.isLiftAreaShutOff.value === "ConesEtc"}
                            onSelect={() => changeHandler("isLiftAreaShutOff", "ConesEtc")}
                            label={resources.get("ConesEtc")}
                            readOnly={!canManageContent}
                            inputId="is-lift-area-shut-off__cones-etc"
                            name="is-lift-area-shut-off"
                            containerClassName="me-3"
                        />

                        <RadioButton
                            checked={formState.isLiftAreaShutOff.value === "notApplicable"}
                            onSelect={() => changeHandler("isLiftAreaShutOff", "notApplicable")}
                            label={resources.get("NotApplicable")}
                            readOnly={!canManageContent}
                            inputId="is-lift-area-shut-off__not-applicable"
                            name="is-lift-area-shut-off"
                        />
                    </div>
                </div>
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-work-done-beneath-load"
                    labelText={resources.get("IsWorkDoneBeneathLoad")}
                    onChange={(updatedValue) => changeHandler("isWorkDoneBeneathLoad", updatedValue)}
                    resources={resources}
                    value={formState.isWorkDoneBeneathLoad.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-lifting-hook-open"
                    labelText={resources.get("IsLiftingHookOpen")}
                    onChange={(updatedValue) => changeHandler("isLiftingHookOpen", updatedValue)}
                    resources={resources}
                    value={formState.isLiftingHookOpen.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-support-legs-correct"
                    labelText={resources.get("IsSupportLegsCorrect")}
                    onChange={(updatedValue) => changeHandler("isSupportLegsCorrect", updatedValue)}
                    resources={resources}
                    value={formState.isSupportLegsCorrect.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-ground-got-bearing-capacity"
                    labelText={resources.get("IsGroundGotBearingCapcity")}
                    onChange={(updatedValue) => changeHandler("isGroundGotBearingCapcity", updatedValue)}
                    resources={resources}
                    value={formState.isGroundGotBearingCapcity.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-lift-in-place"
                    labelText={resources.get("IsLiftPlanInPlace")}
                    onChange={(updatedValue) => changeHandler("isLiftPlanInPlace", updatedValue)}
                    resources={resources}
                    value={formState.isLiftPlanInPlace.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-risk-analysis-done"
                    labelText={resources.get("IsRiskanalysDone")}
                    onChange={(updatedValue) => changeHandler("isRiskanalysDone", updatedValue)}
                    resources={resources}
                    value={formState.isRiskanalysDone.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-signalman-chosen"
                    labelText={resources.get("IsSignalmanChoosen")}
                    onChange={(updatedValue) => changeHandler("isSignalmanChoosen", updatedValue)}
                    resources={resources}
                    value={formState.isSignalmanChoosen.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-control-line-available"
                    labelText={resources.get("IsControlLineAvailable")}
                    onChange={(updatedValue) => changeHandler("isControlLineAvailable", updatedValue)}
                    resources={resources}
                    value={formState.isControlLineAvailable.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-vehicle-around-rorgatan"
                    labelText={resources.get("IsVehicleAroundRorgatan")}
                    onChange={(updatedValue) => changeHandler("isVehicleAroundRorgatan", updatedValue)}
                    resources={resources}
                    value={formState.isVehicleAroundRorgatan.value}
                    readOnly={!canManageContent}
                />
            </li>
        </ul>
    );
}

function ChecklistVersion202602({ resources, formState, changeHandler, canManageContent }: ChecklistProps) {
    return (
        <ul className="list-unstyled">
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-infrastructure-department-contacted"
                    labelText={resources.get("IsInfrastructureDepartmentContacted")}
                    onChange={(updatedValue) => changeHandler("isInfrastructureDepartmentContacted", updatedValue)}
                    resources={resources}
                    value={formState.isInfrastructureDepartmentContacted.value}
                    readOnly={!canManageContent}
                />
                <p className="w-50">
                    <small>{resources.get("IsInfrastructureDepartmentContactedSubText")}</small>
                </p>
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-approved-by-infra"
                    labelText={resources.get("IsApprovedByInfra")}
                    onChange={(updatedValue) => changeHandler("isApprovedByInfra", updatedValue)}
                    resources={resources}
                    value={formState.isApprovedByInfra.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2 d-flex justify-content-between">
                <span className="w-50">{resources.get("IfApprovedByInfraName")}</span>
                <div className="d-flex align-items-center">
                    <label htmlFor="if-approved-by-infra-name">{resources.get("Name")}</label>
                    <input
                        id="if-approved-by-infra-name"
                        className={`form-control ms-1 me-3 ${!canManageContent ? "read-only-input" : ""}`}
                        type="text"
                        style={{ width: "140px" }}
                        value={formState.ifApprovedByInfraName.value ?? ""}
                        onChange={(e) => changeHandler("ifApprovedByInfraName", e.target.value)}
                        readOnly={!canManageContent}
                    />
                    <label htmlFor="if-approved-by-infra-number">{resources.get("Tel")}</label>
                    <input
                        id="if-approved-by-infra-number"
                        className={`form-control ms-1 ${!canManageContent ? "read-only-input" : ""}`}
                        type="text"
                        style={{ width: "140px" }}
                        placeholder={resources.get("PhoneNumberInputPlaceholder")}
                        value={formState.ifApprovedByInfraNumber.value ?? ""}
                        onChange={(e) => changeHandler("ifApprovedByInfraNumber", e.target.value)}
                        readOnly={!canManageContent}
                    />
                </div>
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="has-infra-done-exposures-on-quay"
                    labelText={resources.get("HasInfraDoneExposuresOnQuay")}
                    onChange={(updatedValue) => changeHandler("hasInfraDoneExposuresOnQuay", updatedValue)}
                    resources={resources}
                    value={formState.hasInfraDoneExposuresOnQuay.value}
                    readOnly={!canManageContent}
                />
                <p className="w-50">
                    <small>{resources.get("HasInfraDoneExposuresOnQuayInfo")}</small>
                </p>
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-crane-operator-educated"
                    labelText={resources.get("IsCrainOperatorEducated")}
                    onChange={(updatedValue) => changeHandler("isCrainOperatorEducated", updatedValue)}
                    resources={resources}
                    value={formState.isCrainOperatorEducated.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-open-lifting-hook-used"
                    labelText={resources.get("IsOpenLiftingHookUsed")}
                    onChange={(updatedValue) => changeHandler("isOpenLiftingHookUsed", updatedValue)}
                    resources={resources}
                    value={formState.isOpenLiftingHookUsed.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-lift-line-in-good-condition"
                    labelText={resources.get("IsLiftLineInGoodCondition")}
                    onChange={(updatedValue) => changeHandler("isLiftLineInGoodCondition", updatedValue)}
                    resources={resources}
                    value={formState.isLiftLineInGoodCondition.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-lift-line-correct-for-the-weight"
                    labelText={resources.get("IsLiftLineCorrectForTheWeight")}
                    onChange={(updatedValue) => changeHandler("isLiftLineCorrectForTheWeight", updatedValue)}
                    resources={resources}
                    value={formState.isLiftLineCorrectForTheWeight.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-equipment-tested"
                    labelText={resources.get("IsEquipmentTested")}
                    onChange={(updatedValue) => changeHandler("isEquipmentTested", updatedValue)}
                    resources={resources}
                    value={formState.isEquipmentTested.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <div className="d-flex justify-content-between form-group">
                    <div>{resources.get("IsLiftAreaShutOff")}</div>
                    <div className="d-flex">
                        <LabelCheckbox
                            label={resources.get("ClosedAreaTape")}
                            checked={formState.isLiftAreaShutOff_Tape.value}
                            onChange={(checked) => changeHandler("isLiftAreaShutOff_Tape", checked)}
                            readOnly={!canManageContent}
                            id="is-lift-area-shut-off__tape"
                            containerClassName="me-4"
                        />
                        <LabelCheckbox
                            label={resources.get("ConesEtc")}
                            checked={formState.isLiftAreaShutOff_Cones.value}
                            onChange={(checked) => changeHandler("isLiftAreaShutOff_Cones", checked)}
                            readOnly={!canManageContent}
                            id="is-lift-area-shut-off__cones"
                            containerClassName="me-4"
                        />
                        <LabelCheckbox
                            label={resources.get("NotApplicable")}
                            checked={formState.isLiftAreaShutOff_NA.value}
                            onChange={(checked) => changeHandler("isLiftAreaShutOff_NA", checked)}
                            readOnly={!canManageContent}
                            id="is-lift-area-shut-off__na"
                            containerClassName="me-4"
                        />
                    </div>
                </div>
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-work-done-beneath-load"
                    labelText={resources.get("IsWorkDoneBeneathLoad")}
                    onChange={(updatedValue) => changeHandler("isWorkDoneBeneathLoad", updatedValue)}
                    resources={resources}
                    value={formState.isWorkDoneBeneathLoad.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-support-legs-correct"
                    labelText={resources.get("IsSupportLegsCorrect")}
                    onChange={(updatedValue) => changeHandler("isSupportLegsCorrect", updatedValue)}
                    resources={resources}
                    value={formState.isSupportLegsCorrect.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-ground-got-bearing-capacity"
                    labelText={resources.get("IsGroundGotBearingCapcity")}
                    onChange={(updatedValue) => changeHandler("isGroundGotBearingCapcity", updatedValue)}
                    resources={resources}
                    value={formState.isGroundGotBearingCapcity.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-lift-in-place"
                    labelText={resources.get("IsLiftPlanInPlace")}
                    onChange={(updatedValue) => changeHandler("isLiftPlanInPlace", updatedValue)}
                    resources={resources}
                    value={formState.isLiftPlanInPlace.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-risk-analysis-done"
                    labelText={resources.get("IsRiskanalysDone")}
                    onChange={(updatedValue) => changeHandler("isRiskanalysDone", updatedValue)}
                    resources={resources}
                    value={formState.isRiskanalysDone.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-signalman-chosen"
                    labelText={resources.get("IsSignalmanChoosen")}
                    onChange={(updatedValue) => changeHandler("isSignalmanChoosen", updatedValue)}
                    resources={resources}
                    value={formState.isSignalmanChoosen.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-control-line-available"
                    labelText={resources.get("IsControlLineAvailable")}
                    onChange={(updatedValue) => changeHandler("isControlLineAvailable", updatedValue)}
                    resources={resources}
                    value={formState.isControlLineAvailable.value}
                    readOnly={!canManageContent}
                />
            </li>
            <li className="m-2">
                <ChecklistRadioOptions
                    id="is-vehicle-around-rorgatan"
                    labelText={resources.get("IsVehicleAroundRorgatan")}
                    onChange={(updatedValue) => changeHandler("isVehicleAroundRorgatan", updatedValue)}
                    resources={resources}
                    value={formState.isVehicleAroundRorgatan.value}
                    readOnly={!canManageContent}
                />
            </li>
        </ul>
    );
}

export default HeavyLiftingChecklistModal;
